import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  differenceInMonths,
  isSameMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  format,
  formatISO,
  isBefore,
} from 'date-fns';
import moment from 'moment';
import { useQuery } from 'react-query';
import Report from '../../../Layout/Report';
import { addLoadingList, removeLoadingList, setLoadingDescription } from '../../../../reducers/notification';
import MassMessageExpandRow from './components/MassMessageExpandRow';
import useSearchParams from '../../../../hooks/useSearchParams';
import formatNumberAsCurrency from '../../../../utils/formatNumberAsCurrency';
import { getMassMessages } from '../../../../services/massMessages';
import { getMassMessageAttributionSummary } from '../../../../services/massMessageReports';

const MassMessage = ({ auth }) => {
  const { setValue: setSearchParams, getAll } = useSearchParams();
  const params = getAll();

  const [filters, setFilters] = useState({
    associate: [],
  });
  const [dateRange, setDateRange] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pageSizeTable, setPageSizeTable] = useState(10);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const paramsFilters = {
      associate: params?.associate ? params?.associate : [],
    };
    setFilters(paramsFilters);
    if (params?.startDate && params?.endDate) {
      setDateRange([new Date(params.startDate), new Date(params.endDate)]);
    }
    // No default — ReportSelectDate fires onChange on mount and drives the initial date range
  }, [window.location.search]);

  const getDefaultPeriod = useMemo(() => {
    if (params?.startDate && params?.endDate) {
      if (
        isSameMonth(new Date(params.endDate), new Date(params.startDate)) &&
        isFirstDayOfMonth(new Date(params.startDate)) &&
        isLastDayOfMonth(new Date(params.endDate)) &&
        differenceInMonths(new Date(), new Date(params.startDate)) === 1
      ) {
        return 'previousmonth';
      }
      return `${format(new Date(params.startDate), 'MM/dd/yyyy')} - ${format(new Date(params.endDate), 'MM/dd/yyyy')}`;
    }
    return 'previousmonth';
  }, []);

  const handleOnDateRangeChange = useCallback(dates => {
    setSearchParams('startDate', formatISO(dates[0]));
    setSearchParams('endDate', formatISO(dates[1]));
  }, []);

  const queryParams = useMemo(() => {
    const params = {};
    if (dateRange[0]) params.scheduledAfter = formatISO(dateRange[0]);
    if (dateRange[1]) params.scheduledBefore = formatISO(dateRange[1]);
    if (filters.associate.length === 1) params.associateId = filters.associate[0];
    return params;
  }, [dateRange, filters]);

  const {
    data: listResponse,
    isLoading: isLoadingList,
  } = useQuery(
    ['massMessageReportList', queryParams],
    () => getMassMessages({ query: { ...queryParams, statuses: ['COMPLETED', 'CANCELED'], minTotalSent: 1, limit: 1000 }, loadingStatusOff: true }),
    {
      enabled: !!dateRange[0] && !!dateRange[1],
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 60 * 1000,
    },
  );

  // Step 1: apply associate filter to the raw list before deriving IDs.
  // The API only supports a single associateId param, so multi-select is client-side.
  // This must happen before massMessageIds so attribution is scoped to the filtered set.
  const filteredList = useMemo(() => {
    if (!listResponse?.data) return [];
    if (filters.associate.length > 1) {
      return listResponse.data.filter(item => filters.associate.includes(String(item.associate?.id)));
    }
    return listResponse.data;
  }, [listResponse, filters]);

  // Step 2: IDs come from the filtered list so attribution always matches visible rows.
  const massMessageIds = useMemo(() => filteredList.map(item => item.id), [filteredList]);

  const {
    data: attributionSummary,
    isLoading: isLoadingAttribution,
  } = useQuery(
    ['massMessageAttributionSummary', massMessageIds],
    () => getMassMessageAttributionSummary({ massMessageIds }),
    {
      enabled: !!auth.pos_type && massMessageIds.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 60 * 1000,
    },
  );

  const isLoading = isLoadingList || (!!auth.pos_type && isLoadingAttribution);

  useEffect(() => {
    if (isLoading) {
      setLoadingDescription("Sit tight! We're compiling your report.");
      addLoadingList('massMsgReport');
    } else {
      removeLoadingList('massMsgReport');
      setLoadingDescription(null);
    }
  }, [isLoading]);

  // Step 3: build attribution lookup from the summary.
  const attributionByMessageId = useMemo(() => {
    if (!attributionSummary?.attributions) return {};
    const map = {};
    attributionSummary.attributions.forEach(attr => {
      map[attr.massMessageId] = attr;
    });
    return map;
  }, [attributionSummary]);

  // Step 4: merge attribution into the filtered list.
  // The API returns associate as { id, name } where name is the pre-combined full name.
  const tableData = useMemo(() => filteredList.map(item => ({
    ...item,
    massMessageId: item.id,
    associateName: item.associate?.name || '',
    audienceSize: item.audience?.audienceCount || 0,
    attribution: attributionByMessageId[item.id] || null,
  })), [filteredList, attributionByMessageId]);

  const handleFilterChange = useCallback(
    value => {
      const updatedFilters = {
        deliveryMethod: [],
        associate: [],
      };
      value.forEach(item => {
        if (item[0] && item[1]) {
          updatedFilters[item[0]] = [...updatedFilters[item[0]], item[1]];
        } else {
          updatedFilters[item[0]] = [];
        }
      });
      Object.keys(updatedFilters).forEach(key => {
        setSearchParams(key, updatedFilters[key].join(','));
      });
    },
    [filters],
  );

  const removeFilter = useCallback(
    (key, value) => {
      const updatedFilters = { ...filters };
      updatedFilters[key] = updatedFilters[key].filter(filter => filter !== value);
      Object.keys(updatedFilters).forEach(key => {
        setSearchParams(key, updatedFilters[key].join(','));
      });
    },
    [filters],
  );

  const stats = useMemo(() => {
    if (!tableData.length && !listResponse?.data) return [];

    const totalSent = tableData.reduce((sum, item) => sum + (item.totalSent || 0), 0);
    const totalMassMessages = tableData.length;

    const relatedSales = auth.pos_type && attributionSummary
      ? [
          {
            title: 'Related Sales',
            value: formatNumberAsCurrency((attributionSummary.totalAttributedRevenue || 0).toFixed(2)),
            description: (
              <span className="fs-14 mid-gray m-top-3">
                {`Across ${attributionSummary.totalAttributedSalesCount || 0} transactions`}
              </span>
            ),
          },
        ]
      : [];

    return [
      {
        title: 'Total',
        value: totalSent,
        description: (
          <span className="fs-14 mid-gray m-top-3">
            {`Across ${totalMassMessages} messages`}
          </span>
        ),
      },
      ...relatedSales,
    ];
  }, [tableData, listResponse, attributionSummary, auth?.pos_type]);

  const graphData = useMemo(() => {
    if (!tableData.length) return {};

    // Build chart data client-side by grouping list items by date
    const dateMap = {};
    tableData.forEach(item => {
      const date = format(new Date(item.scheduledDateTime), 'yyyy-MM-dd');
      if (!dateMap[date]) {
        dateMap[date] = { totalSent: 0, revenue: 0 };
      }
      dateMap[date].totalSent += item.totalSent || 0;
      dateMap[date].revenue += item.attribution?.revenue || 0;
    });

    const chartPoints = Object.entries(dateMap)
      .map(([date, values]) => ({ date, ...values }))
      .sort((a, b) => a.date.localeCompare(b.date));

    const data = {};
    if (auth.pos_type) {
      data['Related Sales'] = chartPoints.map(point => ({
        date: point.date,
        total: point.revenue,
      }));
    } else {
      data.Delivered = chartPoints.map(point => ({
        date: point.date,
        total: point.totalSent,
      }));
    }
    return data;
  }, [tableData, auth.pos_type]);

  const columns = useMemo(() => {
    const getAssociateName = record => record?.associateName || 'Unknown Associate';

    const posTypeColumns = [
      {
        title: 'Sent',
        dataIndex: 'scheduledDateTime',
        key: 'scheduledDateTime',
        key_export: 'scheduled_date_time',
        sorter: (a, b) => new Date(a.scheduledDateTime) - new Date(b.scheduledDateTime),
        render: value => moment.utc(new Date(value)).format('MMM DD, yyyy'),
        defaultSortOrder: 'descend',
      },
      {
        title: 'Created by',
        dataIndex: 'associateName',
        key: 'associateName',
        key_export: 'created_by',
        render: (_, record) => getAssociateName(record),
        sorter: (a, b) => getAssociateName(a).localeCompare(getAssociateName(b)),
      },
      {
        title: 'Audience Size',
        dataIndex: 'audienceSize',
        key: 'audienceSize',
        key_export: 'audience_size',
        sorter: (a, b) => (a.audienceSize || 0) - (b.audienceSize || 0),
      },
      {
        title: 'Delivered',
        dataIndex: 'totalSent',
        key: 'totalSent',
        key_export: 'delivered',
        sorter: (a, b) => (a.totalSent || 0) - (b.totalSent || 0),
      },
      {
        title: 'Purchase',
        dataIndex: ['attribution', 'salesCount'],
        key: 'purchases',
        sorter: (a, b) => (a.attribution?.salesCount || 0) - (b.attribution?.salesCount || 0),
        render: value => value || 0,
        hidden: !auth.pos_type,
      },
      {
        title: 'Related Sales',
        dataIndex: ['attribution', 'revenue'],
        key: 'relatedSales',
        key_export: 'related_sales',
        sorter: (a, b) => (a.attribution?.revenue || 0) - (b.attribution?.revenue || 0),
        render: value => formatNumberAsCurrency(value || 0),
        hidden: !auth.pos_type,
      },
    ];

    const nonPosTypeColumns = [
      {
        title: 'Sent',
        dataIndex: 'scheduledDateTime',
        key: 'scheduledDateTime',
        key_export: 'scheduled_date_time',
        sorter: (a, b) => new Date(a.scheduledDateTime) - new Date(b.scheduledDateTime),
        render: value => moment.utc(new Date(value)).format('MMM DD, yyyy'),
        defaultSortOrder: 'descend',
      },
      {
        title: 'Created by',
        dataIndex: 'associateName',
        key: 'associateName',
        key_export: 'created_by',
        render: (_, record) => getAssociateName(record),
        sorter: (a, b) => getAssociateName(a).localeCompare(getAssociateName(b)),
      },
      {
        title: 'Audience Size',
        dataIndex: 'audienceSize',
        key: 'audienceSize',
        key_export: 'audience_size',
      },
      {
        title: 'Delivered',
        dataIndex: 'totalSent',
        key: 'totalSent',
        key_export: 'delivered',
      },
    ];

    return auth.pos_type ? posTypeColumns : nonPosTypeColumns;
  }, [auth]);

  const expandedRowRender = useCallback(
    record => {
      return (
        <MassMessageExpandRow
          massMessageId={record.massMessageId}
          associateName={record.associateName || ''}
          scheduledDateTime={record.scheduledDateTime}
          audienceSize={record.audienceSize}
          message={record.message}
          posType={!!auth?.pos_type}
        />
      );
    },
    [auth?.pos_type],
  );

  const handleExpandRow = useCallback(
    (expand, record) => {
      let updatedExpandedRowKeys = [...expandedRowKeys];
      if (expand) {
        updatedExpandedRowKeys.push(record.massMessageId);
        setExpandedRowKeys(updatedExpandedRowKeys);
      } else {
        updatedExpandedRowKeys = updatedExpandedRowKeys.filter(key => key !== record.massMessageId);
        setExpandedRowKeys(updatedExpandedRowKeys);
      }
    },
    [expandedRowKeys],
  );

  const dataCSVExport = useMemo(() => {
    return auth?.pos_type
      ? tableData.map(item => ({
          scheduled_date_time: moment.utc(new Date(item.scheduledDateTime)).format('MMM DD, yyyy'),
          created_by: item.associateName || 'Unknown Associate',
          audience_size: item.audienceSize || 0,
          delivered: item.totalSent || 0,
          purchases: item.attribution?.salesCount || 0,
          related_sales: formatNumberAsCurrency(item.attribution?.revenue || 0),
        }))
      : tableData.map(item => ({
          scheduled_date_time: moment.utc(new Date(item.scheduledDateTime)).format('MMM DD, yyyy'),
          created_by: item.associateName || 'Unknown Associate',
          audience_size: item.audienceSize || 0,
          delivered: item.totalSent || 0,
        }));
  }, [tableData, auth?.pos_type]);

  return (
    <Report
      title="Mass Message"
      stats={stats}
      options={[]}
      hasStoreFilter={false}
      hasCSVExport
      CSVtable={dataCSVExport}
      CSVFilename="mass-message.csv"
      handleFilterChange={handleFilterChange}
      filters={filters}
      removeFilter={removeFilter}
      onDateRangeChange={handleOnDateRangeChange}
      dataRange={dateRange}
      defaultPeriodValue={getDefaultPeriod}
      graphData={graphData}
      columns={columns}
      table={tableData}
      hasBanner={isBefore(dateRange[0], new Date('2024-04-25'))}
      textBanner="Clientbook started to track that information as of April 25th, 2024. You are unable to see the information before that date."
      tablesProps={{
        pagination: {
          total: tableData.length,
          showSizeChanger: { showSearch: false },
          showTotal: total => `Total ${total} items`,
          onChange: (newPage, pageSize) => {
            setPage(newPage);
            setPageSizeTable(pageSize);
          },
          page,
          pageSize: pageSizeTable,
        },
        rowKey: 'massMessageId',
        expandable: {
          expandedRowRender: record => expandedRowRender(record),
          defaultExpandedRowKeys: ['0'],
          onExpand: (expanded, record) => handleExpandRow(expanded, record),
          expandedRowKeys,
          expandRowByClick: true,
        },
      }}
    />
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(MassMessage);
